import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CURRENCIES, countryPreset, esPaisConocido, formatMoneyIn } from '@alexiarest/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Card } from '../components/Card';
import { CheckboxField, SearchSelectField, TextField } from '../components/Field';
import { PhoneField } from '../components/PhoneField';
import { countryOptionsFor } from '../countryOptions';
import { useToast } from '../components/Toast';
import { founderAccessCodeFrom } from '../founderInvite';
import { IconoPaso, SetupShell } from './setupShell';

const CURRENCY_OPTIONS = Object.values(CURRENCIES).map((info) => ({
  value: info.code,
  label: info.label,
}));

/**
 * Alta del restaurante para un usuario recien registrado: tiene sesion pero
 * su token no lleva tenant. Es el paso 1 del cuestionario inicial (ver
 * SetupWizardPage): tres campos que crean el restaurante, y a partir de ahi
 * los pasos 2 a 5 salen del tenant recien creado.
 * Al terminar se refrescan los tokens (el claim nuevo no esta en el actual).
 */
export function OnboardingPage(): JSX.Element {
  const { user, logout, refreshSession } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();

  const [name, setName] = useState('');
  const [country, setCountry] = useState('ES');
  const [currency, setCurrency] = useState('EUR');
  const [whatsapp, setWhatsapp] = useState('');
  // Sin marcar por defecto: una casilla premarcada no vale como permiso.
  const [newsletter, setNewsletter] = useState(false);
  const [saving, setSaving] = useState(false);

  const preset = countryPreset(country);

  // La invitación Founder sobrevive al login en la URL (returnTo). Con ella
  // el alta nace sin prueba y bloqueada en la página de pago.
  const founderCode = founderAccessCodeFrom(window.location.search);
  /*
   * El WhatsApp se pide a todos y es obligatorio para los Founder (documento
   * de Alex, 5 sep 2026): es el canal directo para acompañar y pedir
   * feedback en esta primera etapa, y es mejor pedirlo aquí, diciendo para
   * qué, que ir detrás de cada uno después.
   */
  const esFounder = founderCode !== '';

  // Cambiar de pais propone su moneda; lo que el usuario elija despues, manda.
  const onCountry = (value: string) => {
    if (!esPaisConocido(value)) return;
    setCountry(value);
    setCurrency(countryPreset(value).currency);
  };

  const submit = async () => {
    if (!name.trim()) {
      toast.notify('Ponle nombre a tu restaurante.', 'error');
      return;
    }
    if (esFounder && !whatsapp.trim()) {
      toast.notify('Déjanos tu WhatsApp: es como te acompañamos siendo Founder.', 'error');
      return;
    }
    setSaving(true);
    try {
      const result = await api.onboard({
        name: name.trim(),
        country,
        currency,
        founderCode: founderCode || undefined,
        whatsapp: whatsapp.trim() || undefined,
        newsletter,
      });
      if (result.tokensStale) {
        // El ID token actual no lleva el claim del tenant recien creado.
        const ok = await refreshSession();
        if (!ok) {
          toast.notify(
            'Tu restaurante está creado, pero hay que entrar de nuevo para verlo.',
            'info',
          );
          logout();
          return;
        }
      }
      qc.clear();
      toast.success(
        result.tenant.founderPending
          ? `¡${result.tenant.name} está en marcha! Completa el pago Founder para entrar.`
          : `¡${result.tenant.name} está en marcha! 14 días de prueba por delante.`,
      );
    } catch (error) {
      toast.fail(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SetupShell current={1}>
      <div className="stack">
        <div className="setup__intro">
          <span className="setup__intro-icono"><IconoPaso step={1} /></span>
          <h1 className="setup__titulo">
            {user?.name ? `Hola, ${user.name}. ` : ''}Empecemos por tu restaurante
          </h1>
          <p className="setup__texto">
            Cinco pasos y tienes Alexia preparada para tu negocio: 14 días de prueba, sin tarjeta.
            Podrás modificar estos datos más adelante desde Ajustes.
          </p>
        </div>
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
        <Card>
        <div className="form">
          <TextField
            label="Nombre del restaurante"
            required
            value={name}
            onChange={setName}
            placeholder="Casa Mireia"
          />
          <SearchSelectField
            label="País"
            value={country}
            onChange={onCountry}
            options={countryOptionsFor(country)}
            placeholder="Busca por nombre o código…"
          />
          <SearchSelectField
            label="Moneda"
            value={currency}
            onChange={setCurrency}
            options={CURRENCY_OPTIONS}
            placeholder="Busca por nombre, símbolo o código…"
            hint={
              preset.generico
                ? `Ejemplo: ${formatMoneyIn(1234.56, currency)} · El ${preset.taxName} de restauración de ${preset.label} lo ajustas en el siguiente paso.`
                : `Ejemplo: ${formatMoneyIn(1234.56, currency)} · En ${preset.label} el ${preset.taxName} de restauración suele ser del ${preset.vatPct} %.`
            }
          />
          <PhoneField
            label="WhatsApp de contacto"
            required={esFounder}
            value={whatsapp}
            onChange={setWhatsapp}
            defaultCountry={country}
            hint={
              esFounder
                ? 'Como Founder tendrás contacto directo con nosotros por WhatsApp. Utilizaremos este número para mantener abierto tu canal directo con nosotros durante esta primera etapa de Alexia.'
                : 'Utilizaremos este número para mantener abierto tu canal directo con nosotros durante esta primera etapa de Alexia. Solo soporte y feedback, nunca marketing. Puedes dejarlo vacío.'
            }
          />
          <CheckboxField
            label="Quiero recibir por correo novedades de Alexia y consejos para mi restaurante"
            checked={newsletter}
            onChange={setNewsletter}
            hint="Un correo de vez en cuando, sin spam. Podrás darte de baja desde cualquiera de ellos."
          />
        </div>
        </Card>
        <div className="setup__acciones">
          <span />
          <div className="setup__acciones-derecha">
            {/* type=submit dentro del formulario: Enter en cualquier campo envia. */}
            <button type="submit" className="btn btn--lg" disabled={saving}>
              {saving ? 'Creando tu restaurante…' : 'Continuar'}
            </button>
          </div>
        </div>
        </form>
      </div>
    </SetupShell>
  );
}
